var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { inject, provide, ref, shallowRef, watch, isVue2 } from 'vue-demi';
import { isVoidField } from '@formily/core';
import { FormPath } from '@formily/shared';
import { observer } from '@formily/reactive-vue';
import { toJS, reaction } from '@formily/reactive';
import { SchemaOptionsSymbol, FieldSymbol, h, Fragment } from '../shared';
import { useAttach } from '../hooks/useAttach';
import { useField, useForm } from '../hooks';
function isVueOptions(options) {
    return (typeof options.template === 'string' ||
        typeof options.render === 'function' ||
        typeof options.setup === 'function');
}
var wrapFragment = function (childNodes) {
    if (!Array.isArray(childNodes)) {
        return childNodes;
    }
    if (childNodes.length > 1) {
        return h(Fragment, {}, { default: function () { return childNodes; } });
    }
    return childNodes[0];
};
var resolveComponent = function (render, extra) {
    var _a;
    if (extra === undefined || extra === null) {
        return render;
    }
    if (typeof extra === 'string') {
        return function () { return __spreadArray(__spreadArray([], __read(render()), false), [extra], false); };
    }
    // not component
    if (!isVueOptions(extra) && typeof extra !== 'function') {
        return render;
    }
    // for scoped slot
    if (extra.length > 1 || ((_a = extra === null || extra === void 0 ? void 0 : extra.render) === null || _a === void 0 ? void 0 : _a.length) > 1) {
        return function (scopedProps) { return __spreadArray(__spreadArray([], __read(render()), false), [
            h(extra, { props: scopedProps }, {}),
        ], false); };
    }
    return function () { return __spreadArray(__spreadArray([], __read(render()), false), [h(extra, {}, {})], false); };
};
var mergeSlots = function (field, slots, content) {
    var _a;
    var slotNames = Object.keys(slots);
    if (!slotNames.length) {
        if (!content) {
            return {};
        }
        if (typeof content === 'string') {
            return {
                default: resolveComponent(function () { return []; }, content),
            };
        }
    }
    var patchSlot = function (slotName) {
        return function () {
            var _a, _b;
            var originArgs = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                originArgs[_i] = arguments[_i];
            }
            return (_b = (_a = slots[slotName]) === null || _a === void 0 ? void 0 : _a.call(slots, __assign({ field: field, form: field.form }, originArgs[0]))) !== null && _b !== void 0 ? _b : [];
        };
    };
    var patchedSlots = {};
    slotNames.forEach(function (name) {
        patchedSlots[name] = patchSlot(name);
    });
    // for named slots
    if (content && typeof content === 'object' && !isVueOptions(content)) {
        Object.keys(content).forEach(function (key) {
            var _a;
            var child = content[key];
            var slot = (_a = patchedSlots[key]) !== null && _a !== void 0 ? _a : (function () { return []; });
            patchedSlots[key] = resolveComponent(slot, child);
        });
        return patchedSlots;
    }
    // maybe default slot is empty
    patchedSlots['default'] = resolveComponent((_a = patchedSlots['default']) !== null && _a !== void 0 ? _a : (function () { return []; }), content);
    return patchedSlots;
};
var createFieldInVue2 = function (innerCreateField) {
    return function () {
        var res;
        var disposer = reaction(function () {
            res = innerCreateField();
        });
        disposer();
        return res;
    };
};
export default observer({
    name: 'ReactiveField',
    props: {
        fieldType: {
            type: String,
            default: 'Field',
        },
        fieldProps: {
            type: Object,
            default: function () { return ({}); },
        },
    },
    setup: function (props, _a) {
        var slots = _a.slots;
        var formRef = useForm();
        var parentRef = useField();
        var optionsRef = inject(SchemaOptionsSymbol, ref(null));
        var createField = function () {
            var _a, _b, _c, _d, _e;
            return (_b = (_a = formRef === null || formRef === void 0 ? void 0 : formRef.value) === null || _a === void 0 ? void 0 : _a["create".concat(props.fieldType)]) === null || _b === void 0 ? void 0 : _b.call(_a, __assign(__assign({}, props.fieldProps), { basePath: (_d = (_c = props.fieldProps) === null || _c === void 0 ? void 0 : _c.basePath) !== null && _d !== void 0 ? _d : (_e = parentRef.value) === null || _e === void 0 ? void 0 : _e.address }));
        };
        if (isVue2) {
            createField = createFieldInVue2(createField);
        }
        var fieldRef = shallowRef(createField());
        watch(function () { return props.fieldProps; }, function () { return (fieldRef.value = createField()); });
        useAttach(fieldRef);
        provide(FieldSymbol, fieldRef);
        return function () {
            var _a;
            var field = fieldRef.value;
            var options = optionsRef.value;
            if (!field) {
                return (_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots);
            }
            if (field.display !== 'visible') {
                return h('template', {}, {});
            }
            var mergedSlots = mergeSlots(field, slots, field.content);
            var renderDecorator = function (childNodes) {
                var _a;
                if (!field.decoratorType) {
                    return wrapFragment(childNodes);
                }
                var finalComponent = (_a = FormPath.getIn(options === null || options === void 0 ? void 0 : options.components, field.decoratorType)) !== null && _a !== void 0 ? _a : field.decoratorType;
                var componentAttrs = toJS(field.decorator[1]) || {};
                var componentData = {
                    attrs: componentAttrs,
                    style: componentAttrs === null || componentAttrs === void 0 ? void 0 : componentAttrs.style,
                    class: componentAttrs === null || componentAttrs === void 0 ? void 0 : componentAttrs.class,
                };
                delete componentData.attrs.style;
                delete componentData.attrs.class;
                return h(finalComponent, componentData, {
                    default: function () { return childNodes; },
                });
            };
            var renderComponent = function () {
                var _a, _b;
                if (!field.componentType)
                    return wrapFragment((_a = mergedSlots === null || mergedSlots === void 0 ? void 0 : mergedSlots.default) === null || _a === void 0 ? void 0 : _a.call(mergedSlots));
                var component = (_b = FormPath.getIn(options === null || options === void 0 ? void 0 : options.components, field.componentType)) !== null && _b !== void 0 ? _b : field.componentType;
                var originData = toJS(field.component[1]) || {};
                var events = {};
                var originChange = originData['@change'] || originData['onChange'];
                var originFocus = originData['@focus'] || originData['onFocus'];
                var originBlur = originData['@blur'] || originData['onBlur'];
                // '@xxx' has higher priority
                Object.keys(originData)
                    .filter(function (key) { return key.startsWith('on'); })
                    .forEach(function (eventKey) {
                    var eventName = "".concat(eventKey[2].toLowerCase()).concat(eventKey.slice(3));
                    events[eventName] = originData[eventKey];
                });
                Object.keys(originData)
                    .filter(function (key) { return key.startsWith('@'); })
                    .forEach(function (eventKey) {
                    events[eventKey.slice(1)] = originData[eventKey];
                    delete originData[eventKey];
                });
                events.change = function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    if (!isVoidField(field))
                        field.onInput.apply(field, __spreadArray([], __read(args), false));
                    originChange === null || originChange === void 0 ? void 0 : originChange.apply(void 0, __spreadArray([], __read(args), false));
                };
                events.focus = function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    if (!isVoidField(field))
                        field.onFocus.apply(field, __spreadArray([], __read(args), false));
                    originFocus === null || originFocus === void 0 ? void 0 : originFocus.apply(void 0, __spreadArray([], __read(args), false));
                };
                events.blur = function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    if (!isVoidField(field))
                        field.onBlur.apply(field, __spreadArray([], __read(args), false));
                    originBlur === null || originBlur === void 0 ? void 0 : originBlur.apply(void 0, __spreadArray([], __read(args), false));
                };
                var componentData = {
                    attrs: __assign(__assign({ disabled: !isVoidField(field)
                            ? field.pattern === 'disabled' || field.pattern === 'readPretty'
                            : undefined, readOnly: !isVoidField(field)
                            ? field.pattern === 'readOnly'
                            : undefined }, originData), { value: !isVoidField(field) ? field.value : undefined }),
                    style: originData === null || originData === void 0 ? void 0 : originData.style,
                    class: originData === null || originData === void 0 ? void 0 : originData.class,
                    on: events,
                };
                delete componentData.attrs.style;
                delete componentData.attrs.class;
                return h(component, componentData, mergedSlots);
            };
            return renderDecorator([renderComponent()]);
        };
    },
});
//# sourceMappingURL=ReactiveField.js.map